import React, { useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html, RoundedBox } from "@react-three/drei";
import { metaFor } from "@/lib/deviceMeta";

export default function DeviceNode3D({ device, position, onSelect, selected, isNew, label, flagged }) {
  const ref = useRef();
  const ringRef = useRef();
  const [hovered, setHovered] = useState(false);
  const meta = metaFor(device.device_type);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.getElapsedTime();
    const target = hovered || selected ? 1.18 : 1;
    ref.current.scale.x += (target - ref.current.scale.x) * 0.12;
    ref.current.scale.y = ref.current.scale.x;
    ref.current.scale.z = ref.current.scale.x;
    ref.current.position.y = position[1] + Math.sin(t * 1.4 + position[0]) * 0.06;
    if (ringRef.current) {
      const s = 1 + Math.sin(t * 3) * 0.12;
      ringRef.current.scale.set(s, s, s);
    }
  });

  return (
    <group position={position}>
      <group
        ref={ref}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
          document.body.style.cursor = "pointer";
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = "default";
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelect(device);
        }}
      >
        <RoundedBox args={[0.62, 0.62, 0.62]} radius={0.1} smoothness={4} castShadow>
          <meshStandardMaterial
            color={flagged ? "#EF4444" : meta.color}
            roughness={0.35}
            metalness={0.15}
            emissive={flagged ? "#EF4444" : isNew ? "#F59E0B" : meta.color}
            emissiveIntensity={flagged ? 0.55 : isNew ? 0.6 : hovered || selected ? 0.35 : 0.12}
          />
        </RoundedBox>
        {(isNew || flagged) && (
          <mesh ref={ringRef} rotation={[Math.PI / 2, 0, 0]}>
            <torusGeometry args={[0.55, 0.03, 12, 48]} />
            <meshStandardMaterial
              color={flagged ? "#EF4444" : "#F59E0B"}
              emissive={flagged ? "#EF4444" : "#F59E0B"}
              emissiveIntensity={1.6}
            />
          </mesh>
        )}
      </group>

      <Html center distanceFactor={9} position={[0, -0.85, 0]} zIndexRange={[10, 0]}>
        <div
          data-testid={`node-label-${device.ip}`}
          dir="ltr"
          style={{ pointerEvents: "none" }}
          className={`select-none whitespace-nowrap rounded-lg px-2 py-1 text-center shadow-sm backdrop-blur ${
            isNew ? "bg-amber-50/95 ring-1 ring-amber-400" : "bg-white/90 ring-1 ring-black/5"
          }`}
        >
          {label ? (
            <>
              <div className="max-w-[140px] truncate text-[11px] font-bold text-[#0A0A0A]">
                {label}
              </div>
              <div className="font-mono text-[9px] text-[#52525B]">{device.ip}</div>
            </>
          ) : (
            <div className="font-mono text-[11px] font-semibold text-[#0A0A0A]">
              {device.ip}
            </div>
          )}
          {!label && device.hostname && (
            <div className="max-w-[130px] truncate text-[9px] text-[#52525B]">
              {device.hostname}
            </div>
          )}
          {device.mac && (
            <div className="font-mono text-[8px] text-[#94A3B8]">{device.mac}</div>
          )}
        </div>
      </Html>
    </group>
  );
}
